import { isValidUploadIdentityValue, newUploadId } from "@uploads";

export interface UploadContextIdentity {
	profileId?: string;
	tabFamilyId?: string;
	pageLoadId?: string;
}

export function classifyUploadContext(
	current: UploadContextIdentity,
	observed: UploadContextIdentity,
	evidence: { differentClaim?: boolean; overlappingPage?: boolean; reloadPredecessor?: boolean }
): string {
	if (!current.pageLoadId || !observed.pageLoadId) return "unknown";
	if (current.pageLoadId === observed.pageLoadId) return evidence.differentClaim ? "same-page-competing-scope" : "same-page";
	// Storage may have been cleared or initialized concurrently: different IDs are not proof of a different browser.
	if (current.profileId && observed.profileId && current.profileId !== observed.profileId) return "different-profile-identity";
	if (!current.tabFamilyId || !observed.tabFamilyId) return "unknown";
	if (evidence.overlappingPage) {
		if (current.tabFamilyId === observed.tabFamilyId) return "duplicate-tab-detected";
		if (current.profileId && current.profileId === observed.profileId) return "separate-tab-same-profile";
	}
	if (evidence.reloadPredecessor && current.tabFamilyId === observed.tabFamilyId) return "reload-successor";
	return "unknown";
}

const PROFILE_KEY = "contact-admin:upload-diagnostic-profile";
const PREDECESSOR_KEY = "contact-admin:upload-diagnostic-page";
const CHANNEL = "contact-admin:upload-diagnostic-presence-v1";
const EVIDENCE_TTL_MS = 5_000;
const MAX_PEERS = 32;
const MAX_CLAIMS = 128;
const safeId = (value: unknown): string | undefined => (isValidUploadIdentityValue(value) ? value : undefined);

export interface ObservedUploadContext {
	uploadOwnerProfileId?: string;
	uploadOwnerTabFamilyId?: string;
	uploadOwnerPageLoadId?: string;
	uploadOwnerClaimId?: string;
	uploadOwnerPurpose?: string;
}

/** Evidence only: no ownership decision may read this module's results. */
export function createUploadContextDiagnostics(tabFamilyId: string, pageLoadId: string) {
	let profileId: string | undefined;
	let profilePersistence = "unavailable";
	let predecessorPageLoadId: string | undefined;
	let reloaded = false;
	try {
		profileId = safeId(localStorage.getItem(PROFILE_KEY));
		if (!profileId) {
			profileId = newUploadId();
			localStorage.setItem(PROFILE_KEY, profileId);
		}
		profilePersistence = "origin-storage";
	} catch {
		// Absence remains unknown; never substitute a per-page id and call it a profile.
		profileId = undefined;
	}
	try {
		predecessorPageLoadId = safeId(sessionStorage.getItem(PREDECESSOR_KEY));
		sessionStorage.setItem(PREDECESSOR_KEY, pageLoadId);
		reloaded = (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type === "reload";
	} catch {
		predecessorPageLoadId = undefined;
	}
	const identity = { profileId, tabFamilyId: safeId(tabFamilyId), pageLoadId: safeId(pageLoadId) };
	const localClaims = new Map<string, string>();
	const peers = new Map<string, { identity: UploadContextIdentity; observedAt: number }>();
	let channel: BroadcastChannel | undefined;
	let requestId: string | undefined;
	let requestAt = -Infinity;
	let channelStatus = "unavailable";
	const send = (message: Record<string, unknown>) => {
		try {
			channel?.postMessage(message);
		} catch {
			channelStatus = "send-failed";
		}
	};
	const requestPresence = () => {
		if (!channel || Date.now() - requestAt < EVIDENCE_TTL_MS) return;
		requestAt = Date.now();
		try {
			requestId = newUploadId();
			send({ kind: "request", requestId, ...identity });
		} catch {
			requestId = undefined;
			channelStatus = "request-unavailable";
		}
	};
	const start = () => {
		try {
			// An iframe is not positive evidence of a second top-level tab.
			if (window.top !== window) return;
			if (typeof BroadcastChannel !== "undefined") {
				channel = new BroadcastChannel(CHANNEL);
				channelStatus = "available";
				channel.onmessage = ({ data }: MessageEvent<unknown>) => {
					if (!data || typeof data !== "object") return;
					const message = data as Record<string, unknown>;
					const peer = {
						profileId: safeId(message.profileId),
						tabFamilyId: safeId(message.tabFamilyId),
						pageLoadId: safeId(message.pageLoadId)
					};
					if (!peer.pageLoadId || !peer.tabFamilyId || peer.pageLoadId === identity.pageLoadId || !safeId(message.requestId))
						return;
					if (message.kind === "request") {
						send({ kind: "response", requestId: message.requestId, ...identity });
					} else if (
						message.kind === "response" &&
						message.requestId === requestId &&
						Date.now() - requestAt <= EVIDENCE_TTL_MS
					) {
						peers.delete(peer.pageLoadId);
						peers.set(peer.pageLoadId, { identity: peer, observedAt: Date.now() });
						if (peers.size > MAX_PEERS) peers.delete(peers.keys().next().value!);
					}
				};
				requestAt = -Infinity;
				requestPresence();
			}
		} catch {
			channelStatus = "unavailable";
		}
	};
	const stop = () => {
		try {
			channel?.close();
		} catch {
			/* best-effort lifecycle only */
		}
		channel = undefined;
		peers.clear();
	};
	start();
	const resume = () => {
		if (!channel) start();
	};
	if (typeof window !== "undefined") {
		window.addEventListener("pagehide", stop);
		window.addEventListener("pageshow", resume);
	}
	return {
		identity,
		dispose() {
			stop();
			if (typeof window !== "undefined") {
				window.removeEventListener("pagehide", stop);
				window.removeEventListener("pageshow", resume);
			}
		},
		registerClaim(claimId: string, purpose: string) {
			if (!safeId(claimId)) return;
			localClaims.set(claimId, purpose);
			if (localClaims.size > MAX_CLAIMS) localClaims.delete(localClaims.keys().next().value!);
		},
		ownerFields(purpose?: string): ObservedUploadContext {
			return {
				uploadOwnerProfileId: profileId,
				uploadOwnerTabFamilyId: identity.tabFamilyId,
				uploadOwnerPageLoadId: identity.pageLoadId,
				uploadOwnerPurpose: purpose
			};
		},
		describe(observed: ObservedUploadContext = {}, currentClaimId?: string, purpose = "unknown"): Record<string, unknown> {
			requestPresence();
			const observedClaimId = safeId(observed.uploadOwnerClaimId);
			const knownLocalClaim = observedClaimId !== undefined && localClaims.has(observedClaimId);
			const observedPurpose = knownLocalClaim ? localClaims.get(observedClaimId!) : observed.uploadOwnerPurpose;
			const observedIdentity = knownLocalClaim
				? identity
				: {
						profileId: safeId(observed.uploadOwnerProfileId),
						tabFamilyId: safeId(observed.uploadOwnerTabFamilyId),
						pageLoadId: safeId(observed.uploadOwnerPageLoadId)
					};
			const peer = observedIdentity.pageLoadId ? peers.get(observedIdentity.pageLoadId) : undefined;
			const peerAgeMs = peer ? Date.now() - peer.observedAt : undefined;
			const overlappingPage =
				peerAgeMs !== undefined &&
				peerAgeMs >= 0 &&
				peerAgeMs <= EVIDENCE_TTL_MS &&
				peer?.identity.tabFamilyId === observedIdentity.tabFamilyId;
			return {
				profileId,
				tabFamilyId: identity.tabFamilyId,
				pageLoadId: identity.pageLoadId,
				profilePersistence,
				channelStatus,
				browserInstanceRelation: "unknown",
				currentClaimId: safeId(currentClaimId),
				observedClaimId,
				observedProfileId: observedIdentity.profileId,
				observedTabFamilyId: observedIdentity.tabFamilyId,
				observedPageLoadId: observedIdentity.pageLoadId,
				contextRelation: classifyUploadContext(identity, observedIdentity, {
					differentClaim: !!observedClaimId && !!currentClaimId && observedClaimId !== currentClaimId,
					overlappingPage,
					reloadPredecessor: reloaded && predecessorPageLoadId === observedIdentity.pageLoadId
				}),
				contextEvidence: knownLocalClaim
					? "local-claim-registry"
					: overlappingPage
						? "broadcast-response"
						: "persisted-owner-metadata",
				peerEvidenceAgeMs: peerAgeMs,
				observedAt: Date.now(),
				uploadLane: purpose,
				preparationPhase: purpose === "pre-persist" ? "pre-persist" : "not-preparing",
				observedUploadLane:
					observedPurpose && ["pre-persist", "direct-upload", "resume"].includes(observedPurpose) ? observedPurpose : "unknown",
				visibilityState: typeof document === "undefined" ? "unknown" : document.visibilityState
			};
		}
	};
}

let singleton: ReturnType<typeof createUploadContextDiagnostics> | undefined;
export function getUploadContextDiagnostics(tabFamilyId: string, pageLoadId: string) {
	return (singleton ??= createUploadContextDiagnostics(tabFamilyId, pageLoadId));
}
